/**
 * Service pour la gestion des adresses d'une organisation.
 *
 * Fournit des méthodes pour :
 * - createOrgAddress   : créer une adresse pour une organisation.
 * - searchOrgAddresses : lister les adresses d'une organisation.
 * - getOrgAddressById  : récupérer une adresse par son ID.
 * - updateOrgAddress   : mettre à jour une adresse.
 * - deleteOrgAddress   : supprimer une adresse.
 *
 * Utilise axiosInstance pour les requêtes HTTP avec interceptors configurés.
 */
import type { AxiosResponse } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type {
  IOrganisationListAddress,
  ICreateOrgAddressBody,
  IUpdateOrgAddressBody,
} from '@/types/manageOrgCommerce.types';

export const OrgAddressService = {
  /**
   * Crée une adresse pour une organisation.
   * @param organizationId - UUID de l'organisation
   * @param data - Corps de la requête
   */
  createOrgAddress: async (
    organizationId: string,
    data: ICreateOrgAddressBody,
  ): Promise<
    AxiosResponse<{ data: IOrganisationListAddress; message: string }>
  > => {
    try {
      const response = await axiosInstance.post<{
        data: IOrganisationListAddress;
        message: string;
      }>(`organizations/${organizationId}/addresses/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Liste les adresses d'une organisation.
   * @param organizationId - UUID de l'organisation
   */
  searchOrgAddresses: async (
    organizationId: string,
  ): Promise<AxiosResponse<IOrganisationListAddress[]>> => {
    try {
      const response = await axiosInstance.get<IOrganisationListAddress[]>(
        `organizations/${organizationId}/addresses/search`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Récupère une adresse par son ID.
   * @param organizationId - UUID de l'organisation
   * @param addressId - UUID de l'adresse
   */
  getOrgAddressById: async (
    organizationId: string,
    addressId: string,
  ): Promise<AxiosResponse<IOrganisationListAddress>> => {
    try {
      const response = await axiosInstance.get<IOrganisationListAddress>(
        `organizations/${organizationId}/addresses/get/${addressId}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Met à jour une adresse d'organisation.
   * @param organizationId - UUID de l'organisation
   * @param addressId - UUID de l'adresse
   * @param data - Corps de la requête
   */
  updateOrgAddress: async (
    organizationId: string,
    addressId: string,
    data: IUpdateOrgAddressBody,
  ): Promise<
    AxiosResponse<{ data: IOrganisationListAddress; message: string }>
  > => {
    try {
      const response = await axiosInstance.put<{
        data: IOrganisationListAddress;
        message: string;
      }>(
        `organizations/${organizationId}/addresses/update/${addressId}`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Supprime une adresse d'organisation.
   * @param organizationId - UUID de l'organisation
   * @param addressId - UUID de l'adresse
   */
  deleteOrgAddress: async (
    organizationId: string,
    addressId: string,
  ): Promise<AxiosResponse<{ message: string }>> => {
    try {
      const response = await axiosInstance.delete<{ message: string }>(
        `organizations/${organizationId}/addresses/delete/${addressId}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
